/**
 * "Vraag Claude" — modal that pipes the active diagram (as SVG) through
 * Claude with a user-supplied prompt and shows the redesigned result
 * side-by-side with the original.
 *
 * Two output modes in M2: SVG (Claude generates a new SVG illustration)
 * or Mermaid (Claude generates a flowchart). The third mode (Design via
 * canvas-design skill) is parked pending a strategy decision.
 *
 * Privacy disclaimer in the dialog: the SVG leaves the browser to the AI
 * provider of the installation (Settings → AI, super admin); the server
 * returns a 412 if none is configured.
 */
import { emitArchiMatePlantUML, emitC4PlantUML } from "@ae/domain";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api } from "../../api/client";
import { getRegistry, resolveBilingual } from "../../registry";
import { useSession } from "../../state/session";
import { useWorkspaceData } from "../../state/workspace";
import {
  canvasBackgroundColor,
  captureCanvasPngBase64,
  captureCanvasSvg,
  captureCanvasSvgDataUrl,
} from "../diagrams/exportDiagram";
import { emitDiagramSvg } from "../text-pane/svgEmitter";
import { extractFencedBlock } from "./extractFencedBlock";

type OutputMode = "svg" | "json" | "plantuml";

const SUGGESTION_CHIP_KEYS = [
  "askClaude.suggestions.managers",
  "askClaude.suggestions.business",
  "askClaude.suggestions.customer",
  "askClaude.suggestions.mainProcesses",
  "askClaude.suggestions.layerIcons",
] as const;

interface Props {
  open: boolean;
  onClose: () => void;
}

export function AskClaudeDialog({ open, onClose }: Props) {
  const { t } = useTranslation();
  const lang = useSession((s) => (s.me?.ui_language === "en" ? "en" : "nl")) as "nl" | "en";
  const activeDiagramId = useWorkspaceData((s) => s.activeDiagramId);
  const revision = useWorkspaceData((s) => s.revision);
  void revision;

  const [outputMode, setOutputMode] = useState<OutputMode>("svg");
  /**
   * Hoe lang Claude nadenkt. Begint op de voorkeur uit Instellingen → Account;
   * een andere keuze hier geldt alleen voor deze dialoog.
   */
  const [effort, setEffort] = useState<Effort>("medium");
  useEffect(() => {
    api
      .getUserSettings()
      .then((s) => {
        if (s.ai_effort_preference) setEffort(s.ai_effort_preference);
      })
      .catch(() => undefined);
  }, []);
  /** Wanneer het versturen begon, voor de lopende tijd. */
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [prompt, setPrompt] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    content: string;
    extracted: boolean;
    raw: string;
    output_mode: OutputMode;
  } | null>(null);
  const promptRef = useRef<HTMLTextAreaElement>(null);

  // Compute all three representations fresh whenever the dialog is
  // opened. SVG is the visual snapshot, PlantUML is the semantic
  // source-of-truth, the JSON summary fills in attributes / descriptions
  // that PlantUML doesn't carry. Sending all three lets Claude reason
  // about the diagram with full ArchiMate semantics rather than
  // guessing from coloured rectangles.
  const inputs = useMemo(() => {
    if (!open || !activeDiagramId) return null;
    const store = useWorkspaceData.getState().store;
    const diagram = store.getDiagram(activeDiagramId);
    if (!diagram) return null;
    const allConcepts = store.listConcepts().filter((c) => !c.tombstoneInBranch);
    const allRelations = store.listRelations().filter((r) => !r.tombstoneInBranch);

    // Scope alle drie de representaties (SVG, PlantUML, JSON-summary)
    // tot het ACTIEVE diagram. Anders zou Claude PlantUML van álle
    // workspace-concepten zien — bv. wanneer er ook een C4-deployment-
    // diagram in dezelfde workspace staat, ook al kijkt de gebruiker
    // naar Example 1.
    const visibleConceptIds = new Set<string>();
    for (const [key, ve] of Object.entries(diagram.viewState)) {
      if (ve.position === undefined) continue;
      visibleConceptIds.add(ve.ref ?? key);
    }
    const concepts = allConcepts.filter((c) => visibleConceptIds.has(c.id));
    // Set van concept-id's die ECHT bestaan (niet-tombstoned + op
    // dit diagram). Gebruikt om orphan-relaties uit te filteren —
    // een relatie waarvan een endpoint verwijst naar een tombstoned
    // concept dat nog wel een viewState-entry heeft, zou anders door
    // de filter glippen en in PlantUML als ghost-knoopje verschijnen.
    const liveConceptIds = new Set(concepts.map((c) => c.id));

    // Per-diagram-scoping voor relaties: een relatie is alleen
    // zichtbaar op diagram D als `viewState[r.id]` op D bestaat
    // (presence = "geplaatst"). Legacy-relaties zonder enige viewState-
    // entry vallen terug op "overal-zichtbaar". Zelfde regel die
    // Canvas + Onderwater venster gebruiken — anders krijgt Claude
    // relaties die op andere diagrammen zijn getekend zolang hun
    // endpoints toevallig op dit diagram staan.
    const placedRelationIds = new Set<string>();
    for (const d of store.listDiagrams()) {
      for (const r of allRelations) {
        if (d.viewState[r.id] !== undefined) placedRelationIds.add(r.id);
      }
    }
    const relations = allRelations.filter((r) => {
      if (!liveConceptIds.has(r.sourceConceptId)) return false;
      if (!liveConceptIds.has(r.targetConceptId)) return false;
      const ve = diagram.viewState[r.id];
      if (ve?.hidden === true) return false;
      const isLegacy = !placedRelationIds.has(r.id);
      const onThisDiagram = ve !== undefined;
      if (!isLegacy && !onThisDiagram) return false;
      return true;
    });

    // Containment-map voor PlantUML brace-block emit. Combineert twee
    // signalen:
    //   1. Expliciete `viewState.containerId` (drag-naar-binnen-actie of
    //      paste-from-plantuml met brace-blocks)
    //   2. Geometrische overlap — als het middelpunt van een concept
    //      binnen de bounding-box van een Grouping ligt, beschouwen we
    //      'm als kind van die Grouping. Bij meerdere overlappende
    //      Groupings wint de kleinste (meest-specifieke).
    // Dit zorgt dat een diagram waar de gebruiker de Groupings rond
    // bestaande concepten heeft getekend (zonder ze formeel in te
    // schuiven) toch met brace-blocks wordt geëmit, zodat Kroki de
    // hiërarchie visueel rendert.
    const containment: Record<string, string> = {};
    for (const c of concepts) {
      const ve = diagram.viewState[c.id];
      if (!ve?.containerId) continue;
      const parentVe = diagram.viewState[ve.containerId];
      const parentConceptId = parentVe?.ref ?? ve.containerId;
      if (parentConceptId === c.id) continue;
      containment[c.id] = parentConceptId;
    }
    // Geometrische detectie — werkt voor C4-stijl (Software System →
    // Container → Component) én voor Groupings. Elk concept (behalve
    // drawing-primitieven) is een potentiële container; "parent ≥ 1.5×
    // kind" voorkomt false-positives wanneer twee concepten van vergelijkbare
    // grootte net overlappen.
    const containerBoxes = concepts
      .filter((g) => !g.typeId.startsWith("drawing."))
      .map((g) => {
        const ve = diagram.viewState[g.id];
        if (!ve?.position) return null;
        const w = ve.width ?? 140;
        const h = ve.height ?? 60;
        return { id: g.id, x: ve.position.x, y: ve.position.y, w, h, area: w * h };
      })
      .filter((b): b is NonNullable<typeof b> => b !== null);
    for (const c of concepts) {
      if (containment[c.id]) continue;
      if (c.typeId.startsWith("drawing.")) continue;
      const ve = diagram.viewState[c.id];
      if (!ve?.position) continue;
      const cw = ve.width ?? 140;
      const ch = ve.height ?? 60;
      const childArea = cw * ch;
      const cx = ve.position.x + cw / 2;
      const cy = ve.position.y + ch / 2;
      let best: { id: string; area: number } | null = null;
      for (const g of containerBoxes) {
        if (g.id === c.id) continue;
        if (g.area < childArea * 1.5) continue;
        if (cx >= g.x && cx <= g.x + g.w && cy >= g.y && cy <= g.y + g.h) {
          if (!best || g.area < best.area) best = { id: g.id, area: g.area };
        }
      }
      if (best) containment[c.id] = best.id;
    }

    const svg = emitDiagramSvg({ diagram, concepts, relations, lang });
    // Diagram-description als `title` in de PlantUML — zelfde als wat
    // de Onderwater venster doet, zodat Claude de korte beschrijving
    // van het diagram als context heeft. Alleen niet-leeg.
    const desc = diagram.description
      ? (diagram.description[lang] ?? diagram.description.nl ?? diagram.description.en ?? "")
      : "";
    const title = desc.trim();
    // View-kind-router: c4.* → C4-emitter (C4-stdlib procedures +
    // include), anders → ArchiMate-emitter. Houdt Ask Claude's PlantUML-
    // weergave in sync met de Onderwater venster (revealText.ts /
    // TextPane.tsx); voorheen viel élke view-kind terug op de ArchiMate-
    // emitter waardoor Claude voor C4-views het verkeerde stdlib zag.
    const isC4View = diagram.viewKind?.startsWith("c4.") ?? false;
    const isSharpView = diagram.viewKind?.startsWith("sharp.") ?? false;
    const plantuml = isC4View
      ? emitC4PlantUML(concepts, relations, {
          lang,
          viewKind: diagram.viewKind,
          ...(title ? { title } : {}),
        })
      : emitArchiMatePlantUML(concepts, relations, {
          lang,
          containment,
          ...(title ? { title } : {}),
          ...(isSharpView ? { includeStereotypeComments: true } : {}),
        });

    const reg = getRegistry();
    const summaryObj = {
      diagram: {
        id: diagram.id,
        name: resolveBilingual(diagram.name, lang) || diagram.id,
        view_kind: diagram.viewKind,
      },
      concepts: concepts.map((c) => {
        const t = reg.getElementType(c.typeId);
        return {
          id: c.id,
          type_id: c.typeId,
          type_label: t ? resolveBilingual(t.label, lang) : c.typeId,
          layer: t?.layer ?? "unknown",
          aspect: t?.aspect ?? "unknown",
          name: c.name,
          description: c.description,
          attributes: c.attributes,
          tags: c.tags,
          owner: c.ownerEmail,
          parent_id: c.parentId ?? null,
        };
      }),
      relations: relations.map((r) => {
        const t = reg.getRelationType(r.typeId);
        return {
          id: r.id,
          type_id: r.typeId,
          type_label: t ? resolveBilingual(t.label, lang) : r.typeId,
          source_concept_id: r.sourceConceptId,
          target_concept_id: r.targetConceptId,
          label: r.label,
          attributes: r.attributes,
        };
      }),
    };
    const summary = JSON.stringify(summaryObj, null, 2);

    return { svg, plantuml, summary };
  }, [open, activeDiagramId, lang, revision]);

  // Bij dialog-open: pak een letterlijke SVG-snapshot van het canvas
  // via html-to-image. Het canvas zit nog onder de overlay (dialog =
  // position:fixed, niet unmount), dus `captureCanvasSvg` vindt de
  // viewport. We tonen de live snapshot in het ORIGINEEL-paneel én
  // versturen 'm naar Claude bij Verzend — beide gebruikers van
  // dezelfde data zodat de gebruiker exact ziet wat Claude ontvangt.
  // Falen → liveSvg blijft null, we vallen terug op de synchrone
  // emitDiagramSvg-output (`inputs.svg`).
  const [liveSvg, setLiveSvg] = useState<string | null>(null);
  // Raw data:image/svg+xml URL voor preview-rendering via <img>.
  // Safari schaalt SVG-roots met intrinsic width/height niet altijd via
  // CSS max-width: 100% — een <img> wel, dus dat is de cross-browser-
  // safe rendering-route. liveSvg blijft de raw SVG-string voor de
  // Claude-payload (eventueel via emitDiagramSvg-fallback).
  const [livePreviewUrl, setLivePreviewUrl] = useState<string | null>(null);
  // PNG-snapshot van het canvas voor multimodal-input. Anthropic
  // tokeniseert images op vrijwel constante kosten (~1500 tokens),
  // wat de visuele context naar Claude brengt zonder de inline-SVG-
  // tekst-bottleneck.
  const [livePng, setLivePng] = useState<string | null>(null);
  const svgSource = liveSvg ?? inputs?.svg ?? "";

  // Esc sluit de modal. Capture-fase zodat React Flow / nested handlers
  // ons niet kunnen pre-empten — sommige inputs binnen de modal (zoals
  // de prompt-textarea) hebben hun eigen onKeyDown maar laten Escape
  // bewust doorvallen; capture verzekert dat we 'm hier altijd zien.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [open, onClose]);

  // Reset state when the dialog closes — next open starts fresh.
  useEffect(() => {
    if (!open) {
      setPrompt("");
      setResult(null);
      setError(null);
      setSubmitting(false);
      setLiveSvg(null);
      setLivePreviewUrl(null);
      setLivePng(null);
      return;
    }
    // Focus the prompt textarea when opening so the user can start typing
    // immediately. Small delay so the modal has finished rendering.
    const t = setTimeout(() => promptRef.current?.focus(), 50);
    // Kick off de twee snapshots parallel — SVG voor preview-display
    // en PNG voor multimodal Claude-input. Beide ~50-300ms.
    let cancelled = false;
    // Canvaskleur als achtergrond: Claude krijgt wat de modelleur ziet.
    const achtergrond = { background: canvasBackgroundColor() };
    void captureCanvasSvg(achtergrond)
      .then((svg) => {
        if (!cancelled) setLiveSvg(svg);
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.warn("captureCanvasSvg failed, preview falls back to emitDiagramSvg", e);
      });
    void captureCanvasSvgDataUrl(achtergrond)
      .then((url) => {
        if (!cancelled) setLivePreviewUrl(url);
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.warn("captureCanvasSvgDataUrl failed, preview falls back to inline-SVG", e);
      });
    void captureCanvasPngBase64(2, achtergrond)
      .then((png) => {
        if (!cancelled) setLivePng(png);
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.warn("captureCanvasPng failed, AI request falls back to text-only context", e);
      });
    return () => {
      clearTimeout(t);
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    if (!prompt.trim() || !inputs) return;
    setSubmitting(true);
    setStartedAt(Date.now());
    setError(null);
    setResult(null);
    try {
      // Welke SVG sturen we? Eerste keuze: de letterlijke canvas-
      // snapshot (`liveSvg`/`svgSource`) — die bevat alle visuele
      // overrides. Maar de echte rem is Claude's context-window
      // (~200K tokens ≈ 500KB tekst), niet de transport-grens. Boven
      // ~500KB raakt de literal-SVG sneller aan Claude's token-limit
      // dan dat 'ie kan reasoneren — dan valt de payload terug op de
      // synthetische SVG (10× compacter, deterministische layout,
      // geen visuele overrides maar genoeg voor Claude om de
      // structuur te zien). PlantUML + JSON-summary leveren toch al
      // de semantiek; de SVG dient als layout-context.
      const LITERAL_SVG_BUDGET = 500 * 1024;
      const literal = svgSource || "";
      let svgPayload = literal;
      let usedFallback = false;
      if (literal.length > LITERAL_SVG_BUDGET || !literal) {
        svgPayload = inputs.svg;
        usedFallback = literal.length > LITERAL_SVG_BUDGET;
      }
      if (usedFallback) {
        // eslint-disable-next-line no-console
        console.warn(
          `aiTransform: literal SVG ${(literal.length / 1024 / 1024).toFixed(1)} MB overschrijdt budget; fallback naar synthetische SVG (${(svgPayload.length / 1024).toFixed(0)} KB).`,
        );
      }
      // PNG ook meesturen voor multimodal-input. ~1500 tokens vast,
      // dus geen budget-issue zelfs bij grote diagrammen. Laat 'm
      // weg als de capture nog niet klaar is — Claude valt dan terug
      // op alleen text/SVG context.
      const imageFields: { image_base64?: string; image_media_type?: "image/png" } = livePng
        ? { image_base64: livePng, image_media_type: "image/png" }
        : {};
      // Streaming-fetch, en rechtstreeks naar de worker: Netlify breekt een
      // doorgestuurd verzoek na ~30 s af, ook als de stroom nog loopt, en een
      // model dat eerst nadenkt heeft vaak langer nodig. Via de proxy (met de
      // sessie-cookie) halen we een ticket van 60 s; daarmee praat de browser
      // met de worker zelf (zie handlers/aiTransform.ts, net als de WebSocket).
      // Kent de worker het ticket nog niet (oudere installatie), dan de oude weg.
      const target = await aiTransformTarget();
      const res = await fetch(target.url, {
        method: "POST",
        headers: { "content-type": "application/json", ...target.headers },
        credentials: target.headers["x-ai-ticket"] ? "omit" : "include",
        body: JSON.stringify({
          svg: svgPayload,
          plantuml: inputs.plantuml,
          summary: inputs.summary,
          prompt: prompt.trim(),
          output_mode: outputMode,
          effort,
          ...imageFields,
        }),
      });
      if (!res.ok) {
        // Niet-ok = JSON-error van de worker (auth/rate-limit/upstream).
        // Stream-pad gebruikt 200 + text/event-stream.
        const body = (await res.json().catch(() => null)) as {
          type?: string;
          title?: string;
          status?: number;
          detail?: string;
        } | null;
        const errorBody = body
          ? {
              type: body.type ?? "about:blank",
              title: body.title ?? "Error",
              status: body.status ?? res.status,
              ...(body.detail ? { detail: body.detail } : {}),
            }
          : null;
        throw new ApiError(res.status, errorBody, body?.detail ?? `HTTP ${res.status}`);
      }
      if (!res.body) throw new Error(t("askClaude.errors.emptyBody"));
      // Het neutrale streamformaat van de worker (`apps/worker/src/ai/llm.ts`),
      // hetzelfde voor elke AI-aanbieder:
      //   event: text   data: {"text":"…"}
      //   event: done   data: {"stop_reason":"…"}
      //   event: error  data: {"message":"…"}
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let raw = "";
      let buffer = "";
      /** Waarom de AI stopte (`end_turn`, `max_tokens`, `refusal`, …). */
      let stopReason: string | null = null;
      const flushUiUpdate = () => {
        // Live-update tijdens streaming: toon de raw-tekst zoals 'ie
        // binnenkomt. extractFencedBlock pas op het einde toepassen,
        // want een halve fence is niets waarde.
        setResult({
          content: raw,
          extracted: false,
          raw,
          output_mode: outputMode,
        });
      };
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let sep = buffer.indexOf("\n\n");
        while (sep !== -1) {
          const block = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          sep = buffer.indexOf("\n\n");
          const lines = block.split("\n");
          const eventName = lines
            .find((l) => l.startsWith("event: "))
            ?.slice(7)
            .trim();
          const dataLine = lines.find((l) => l.startsWith("data: "));
          if (!dataLine) continue;
          let data: { text?: string; message?: string; stop_reason?: string | null };
          try {
            data = JSON.parse(dataLine.slice(6)) as typeof data;
          } catch {
            continue;
          }
          if (eventName === "done") {
            stopReason = data.stop_reason ?? null;
            continue;
          }
          if (eventName === "text" && typeof data.text === "string") {
            raw += data.text;
            flushUiUpdate();
          } else if (eventName === "error") {
            throw new Error(data.message ?? t("askClaude.errors.providerAborted"));
          }
        }
      }
      // Geen tekst: zeg waarom, in plaats van een lege "ruwe output".
      if (!raw.trim()) {
        throw new Error(
          stopReason === "max_tokens"
            ? t("askClaude.errors.maxTokens")
            : stopReason === "refusal"
              ? t("askClaude.errors.refusal")
              : stopReason
                ? t("askClaude.errors.noAnswerWithReason", { reason: stopReason })
                : t("askClaude.errors.noAnswer"),
        );
      }
      if (stopReason === "max_tokens") {
        // eslint-disable-next-line no-console
        console.warn("aiTransform: antwoord afgekapt op de maximale lengte");
      }
      // Stream klaar — pas extractFencedBlock toe op de volledige raw.
      const extracted = extractFencedBlock(raw, outputMode);
      setResult({
        content: extracted ?? raw,
        extracted: extracted !== null,
        raw,
        output_mode: outputMode,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        // 412: geen aanmelding bij Anthropic voor deze workspace.
        if (err.status === 412) {
          setError(err.body?.detail ?? t("askClaude.errors.noAuth"));
        } else if (err.status === 429) {
          // 429 kan twee bronnen hebben: (a) de limiet van Anthropic
          // (algemeen "later opnieuw"), (b) onze eigen per-user
          // dagquota (`/admin/ai-rate-limits`). Bij (b) staat er detail
          // met het concrete aantal — toon die letterlijk zodat de user
          // weet welk getal hij heeft bereikt.
          setError(err.body?.detail ?? t("askClaude.errors.dailyLimit"));
        } else {
          setError(err.message);
        }
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      // Full-bleed sizing: viewport minus a fixed margin (100px each
      // side horizontally, 50px each side vertically). User-tuned for
      // their typical display — gives breathing room around the
      // backdrop without sacrificing diagram-pane real estate. No
      // padding on the backdrop itself; the dialog's calc() math
      // already includes the desired margins.
      className="ledger fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="ask-claude-title"
    >
      <div
        className="flex flex-col rounded-lg border border-(--ld-control) bg-(--ld-surface) shadow-2xl"
        style={{
          width: "calc(100vw - 200px)",
          height: "calc(100vh - 100px)",
        }}
      >
        {/* Header */}
        <header className="flex items-center justify-between border-(--ld-border) border-b px-4 py-2.5">
          <div className="flex items-center gap-3">
            <h2 id="ask-claude-title" className="font-semibold text-(--ld-text) text-sm">
              {t("askClaude.title")}
            </h2>
            <span className="text-(--ld-muted) text-xs italic">{t("askClaude.privacyNote")}</span>
          </div>
          <button
            type="button"
            className="rounded px-2 py-0.5 text-(--ld-muted) text-sm hover:bg-(--ld-raised) hover:text-(--ld-text)"
            onClick={onClose}
            aria-label={t("settings.common.close")}
          >
            ✕
          </button>
        </header>

        {/* Body — left pane (input), right pane (result) */}
        <div className="flex min-h-0 flex-1 divide-x divide-(--ld-rule)">
          {/* Left: original + prompt */}
          <section className="flex w-1/2 min-w-0 flex-col">
            <div className="border-(--ld-border) border-b px-4 py-2 text-(--ld-muted) text-xs">
              {t("askClaude.original")}
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-(--ld-raised)">
              <OriginalPreview
                outputMode={outputMode}
                svgSource={svgSource}
                previewUrl={livePreviewUrl}
                plantumlSource={inputs?.plantuml ?? ""}
                summarySource={inputs?.summary ?? ""}
              />
            </div>
            <div className="flex flex-col gap-2 border-(--ld-border) border-t bg-(--ld-surface) px-4 py-3">
              {/* Output-mode segmented control */}
              <div className="flex items-center gap-3 text-xs">
                <span className="font-medium text-(--ld-text)">{t("askClaude.outputLabel")}</span>
                <div className="inline-flex overflow-hidden rounded border border-(--ld-control)">
                  {(["svg", "json", "plantuml"] as OutputMode[]).map((m) => (
                    <button
                      key={m}
                      type="button"
                      className={`px-3 py-1 ${
                        outputMode === m
                          ? "bg-(--ld-primary) font-semibold text-(--ld-primary-ink)"
                          : "bg-(--ld-surface) text-(--ld-text) hover:bg-(--ld-raised)"
                      }`}
                      onClick={() => setOutputMode(m)}
                      aria-pressed={outputMode === m}
                    >
                      {m === "svg"
                        ? t("askClaude.outputSvg")
                        : m === "json"
                          ? t("askClaude.outputJson")
                          : t("askClaude.outputPlantuml")}
                    </button>
                  ))}
                </div>
                <span className="ml-2 font-medium text-(--ld-text)">
                  {t("askClaude.speedLabel")}
                </span>
                <div className="inline-flex overflow-hidden rounded border border-(--ld-control)">
                  {(["low", "medium", "high"] as Effort[]).map((e) => (
                    <button
                      key={e}
                      type="button"
                      className={`px-3 py-1 ${
                        effort === e
                          ? "bg-(--ld-primary) font-semibold text-(--ld-primary-ink)"
                          : "bg-(--ld-surface) text-(--ld-text) hover:bg-(--ld-raised)"
                      }`}
                      onClick={() => setEffort(e)}
                      aria-pressed={effort === e}
                      title={t(EFFORT_HINT_KEY[e])}
                    >
                      {t(EFFORT_LABEL_KEY[e])}
                    </button>
                  ))}
                </div>
              </div>

              {/* Suggestion chips */}
              <div className="flex flex-wrap gap-1">
                {SUGGESTION_CHIP_KEYS.map((k) => (
                  <button
                    key={k}
                    type="button"
                    className="rounded-full border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 text-xs text-(--ld-text) hover:bg-(--ld-raised)"
                    onClick={() => setPrompt(t(k))}
                  >
                    {t(k)}
                  </button>
                ))}
              </div>

              {/* Prompt textarea */}
              <textarea
                ref={promptRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  // Cmd/Ctrl+Enter = submit shortcut
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && !submitting) {
                    e.preventDefault();
                    void submit();
                  }
                }}
                placeholder={t("askClaude.promptPlaceholder")}
                rows={3}
                className="w-full resize-none rounded border border-(--ld-muted) bg-(--ld-surface) px-2 py-1.5 text-sm focus:border-(--ld-accent) focus:outline-none"
              />

              {/* Submit row */}
              <div className="flex items-center justify-between">
                {error ? (
                  <span className="text-(--ld-negative) text-xs">{error}</span>
                ) : (
                  <span className="text-(--ld-muted) text-xs italic">
                    {t(EFFORT_HINT_KEY[effort])}
                  </span>
                )}
                <button
                  type="button"
                  className="rounded bg-(--ld-primary) px-3 py-1 font-semibold text-sm text-(--ld-primary-ink) hover:bg-(--ld-primary-hover) disabled:cursor-not-allowed disabled:bg-(--ld-active)"
                  onClick={() => void submit()}
                  disabled={!prompt.trim() || !inputs || submitting}
                >
                  {submitting ? t("askClaude.busy") : t("askClaude.submit")}
                </button>
              </div>
            </div>
          </section>

          {/* Right: result */}
          <section className="flex w-1/2 min-w-0 flex-col">
            <div className="border-(--ld-border) border-b px-4 py-2 text-(--ld-muted) text-xs">
              {t("askClaude.result")}
            </div>
            <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-(--ld-raised) p-3">
              <ResultPane result={result} submitting={submitting} startedAt={startedAt} />
            </div>
            {result && (
              <div className="flex items-center gap-2 border-(--ld-border) border-t bg-(--ld-surface) px-4 py-2 text-xs">
                <CopyButton text={result.content} label={t("askClaude.copy")} />
                <DownloadButton content={result.content} mode={result.output_mode} />
                <EmailButton
                  content={result.content}
                  mode={result.output_mode}
                  prompt={prompt.trim()}
                />
                {!result.extracted && !submitting && (
                  <span className="ml-auto flex items-center gap-1.5 text-(--ld-notice-ink) text-xs">
                    <span>{t("askClaude.noCleanBlock")}</span>
                    <button
                      type="button"
                      className="rounded border border-transparent bg-(--ld-notice-bg) px-2 py-0.5 font-medium text-(--ld-notice-ink) hover:bg-(--ld-notice-bg) disabled:opacity-50"
                      onClick={() => void submit()}
                      disabled={submitting}
                      title={t("askClaude.retryTitle")}
                    >
                      {t("askClaude.retry")}
                    </button>
                  </span>
                )}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

type Effort = "low" | "medium" | "high";
const EFFORT_LABEL_KEY: Record<Effort, string> = {
  low: "askClaude.effort.low",
  medium: "askClaude.effort.medium",
  high: "askClaude.effort.high",
};
const EFFORT_HINT_KEY: Record<Effort, string> = {
  low: "askClaude.effortHint.low",
  medium: "askClaude.effortHint.medium",
  high: "askClaude.effortHint.high",
};

function ResultPane({
  result,
  submitting,
  startedAt,
}: {
  result: { content: string; output_mode: OutputMode } | null;
  submitting: boolean;
  startedAt: number | null;
}) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!submitting) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [submitting]);
  if (submitting) {
    const seconds = startedAt ? Math.max(0, Math.round((now - startedAt) / 1000)) : 0;
    const written = result?.content.length ?? 0;
    return (
      <div className="flex h-full flex-col items-center justify-center gap-1 text-(--ld-muted) text-sm">
        <span className="animate-pulse">
          {written > 0
            ? t("askClaude.writing", { chars: written.toLocaleString("nl-NL") })
            : t("askClaude.thinking")}
        </span>
        <span className="text-(--ld-muted) text-xs tabular-nums">{seconds} s</span>
      </div>
    );
  }
  if (!result) {
    return (
      <p className="text-(--ld-muted) text-xs italic">
        {t("askClaude.emptyBefore")}
        <kbd>Cmd+Enter</kbd>
        {t("askClaude.emptyMiddle")}
        <em>{t("askClaude.submit")}</em>.
      </p>
    );
  }
  if (result.output_mode === "svg") {
    return (
      <div
        // Same fit-to-pane recipe as the original-pane container —
        // arbitrary selectors override the SVG's own width/height so it
        // scales down (and only up to its native size) within the pane.
        className="flex h-full w-full items-center justify-center [&>svg]:h-auto [&>svg]:w-auto [&>svg]:max-h-full [&>svg]:max-w-full"
        // Trust: the SVG comes from Claude via our worker. We're rendering
        // Claude-generated content unconditionally — for a model output
        // intended to be displayed visually, that's the contract. Er zit
        // geen extra filter tussen (ook de qan-ai-proxy filterde niets).
        dangerouslySetInnerHTML={{ __html: result.content }}
      />
    );
  }
  // JSON / PlantUML: show source as code. Voor PlantUML kan de gebruiker
  // het in het Onderwater venster plakken voor round-trip. Voor JSON is
  // de pre/code-view het eindresultaat — bedoeld om naar een API of
  // data-pijplijn te kopiëren.
  return (
    <pre className="h-full w-full overflow-auto whitespace-pre-wrap break-all font-mono text-[12px] text-(--ld-text)">
      {result.content}
    </pre>
  );
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-1 text-(--ld-text) hover:bg-(--ld-raised)"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          // ignore
        }
      }}
    >
      {copied ? t("askClaude.copied") : label}
    </button>
  );
}

function DownloadButton({ content, mode }: { content: string; mode: OutputMode }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-1 text-(--ld-text) hover:bg-(--ld-raised)"
      onClick={() => {
        const ext = mode === "svg" ? "svg" : mode === "json" ? "json" : "puml";
        const mime =
          mode === "svg" ? "image/svg+xml" : mode === "json" ? "application/json" : "text/plain";
        const blob = new Blob([content], { type: `${mime};charset=utf-8` });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `claude-resultaat.${ext}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }}
    >
      {t("askClaude.download")}
    </button>
  );
}

function EmailButton({
  content,
  mode,
  prompt,
}: {
  content: string;
  mode: OutputMode;
  prompt: string;
}) {
  const { t } = useTranslation();
  const workspaceId = useSession((s) => s.workspaceId);
  const me = useSession((s) => s.me);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    setBusy(true);
    setInfo(null);
    setError(null);
    try {
      const ext = mode === "svg" ? "svg" : mode === "json" ? "json" : "puml";
      const filename = `claude-resultaat.${ext}`;
      const subject = prompt
        ? t("askClaude.email.subject", { prompt: prompt.slice(0, 80) })
        : t("askClaude.email.subjectDefault");
      const introHtml = prompt
        ? `<p>${t("askClaude.email.intro")}</p><blockquote style="border-left:3px solid #cbd5e1;padding-left:12px;color:#475569;margin:0 0 16px;">${escapeHtml(prompt)}</blockquote>`
        : "";
      const res = await api.emailAiResult({
        workspace_id: workspaceId,
        subject,
        ...(introHtml ? { intro_html: introHtml } : {}),
        content_type: mode === "svg" ? "image/svg+xml" : "text/plain",
        content,
        filename,
      });
      setInfo(t("askClaude.email.sent", { recipient: res.recipient }));
      setTimeout(() => setInfo(null), 4000);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 412) {
          setError(t("askClaude.email.noMailConfig"));
        } else {
          setError(err.body?.detail ?? err.body?.title ?? err.message);
        }
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
      setTimeout(() => setError(null), 6000);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-1 text-(--ld-text) hover:bg-(--ld-raised) disabled:opacity-50"
        onClick={() => void send()}
        disabled={busy || !me?.email}
        title={
          me?.email
            ? t("askClaude.email.buttonTitle", { email: me.email })
            : t("askClaude.email.notLoggedIn")
        }
      >
        {busy ? t("askClaude.email.sending") : t("askClaude.email.button")}
      </button>
      {info && <span className="text-(--ld-positive-ink) text-xs">{info}</span>}
      {error && <span className="text-(--ld-negative) text-xs">{error}</span>}
    </>
  );
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * PlantUML/Kroki retourneert syntax-fouten als HTTP-200 met een mini
 * SVG-banner (typisch height ≤ 50px) die de error-tekst in een
 * `<text>`-element bevat — lichtroze achtergrond (`#fff5f7` of
 * vergelijkbaar). Detecteer dat en haal de tekst op zodat we 'm als
 * foutmelding kunnen tonen i.p.v. een visuele banner.
 *
 * Returns null wanneer het er gewoon een normale render is.
 */
function detectKrokiErrorBanner(svg: string): string | null {
  // Eerste signaal: hoogte expliciet klein. PlantUML diagrammen zijn
  // typisch >50px hoog; een error-banner is ~29px.
  const heightMatch = svg.match(/<svg[^>]*\bheight="(\d+)"/);
  const height = heightMatch ? Number.parseInt(heightMatch[1] ?? "0", 10) : 0;
  if (height === 0 || height > 60) return null;
  // Tweede signaal: text-element bevat een error-zin. Walk all <text>
  // contents (PlantUML gebruikt geen tspan in de banner) en plak ze
  // achter elkaar.
  const texts: string[] = [];
  const re = /<text\b[^>]*>([\s\S]*?)<\/text>/g;
  let m: RegExpExecArray | null = re.exec(svg);
  while (m !== null) {
    const inner = (m[1] ?? "")
      .replace(/<[^>]+>/g, "") // strip nested tspan/etc
      .replace(/\s+/g, " ")
      .trim();
    if (inner) texts.push(inner);
    m = re.exec(svg);
  }
  const combined = texts.join(" ").trim();
  if (!combined) return null;
  if (/syntax error|parseexception|cannot find|fatal/i.test(combined)) {
    return `PlantUML/Kroki: ${combined}`;
  }
  // Als de SVG heel klein is en wél tekst bevat, is het waarschijnlijk
  // toch een error-banner — surface 'm.
  return `PlantUML/Kroki: ${combined}`;
}

/**
 * "Origineel"-paneel links in de Vraag-Claude-dialog. Toont één van de
 * drie input-representaties die naar Claude gaan, geschakeld op basis
 * van de gekozen output-mode:
 *
 * - **svg**      → letterlijke canvas-snapshot (default)
 * - **json**     → de JSON-summary (`<pre>`)
 * - **plantuml** → de PlantUML-source met een knop om 'm via Kroki te
 *                  renderen tot SVG (zelfde proxy als `/api/v1/render`).
 *
 * Deze schakeling maakt het transparant wat Claude precies binnen
 * krijgt, en geeft de gebruiker een "fluent" preview voor PlantUML
 * zonder dat-ie zelf naar plantuml.com hoeft te gaan.
 */
function OriginalPreview({
  outputMode,
  svgSource,
  previewUrl,
  plantumlSource,
  summarySource,
}: {
  outputMode: OutputMode;
  svgSource: string;
  previewUrl: string | null;
  plantumlSource: string;
  summarySource: string;
}) {
  const { t } = useTranslation();
  // Kies de bron voor de <img>-rendering: liefst de raw data-URL van
  // captureCanvasSvgDataUrl (geen decode/re-encode), val anders terug
  // op een ad-hoc data-URL gemaakt van de svgSource-string. <img>
  // schaalt consistent over alle browsers — Safari heeft moeite met
  // CSS-sized SVG-roots die intrinsic width/height hebben.
  const previewSrc = useMemo(() => {
    if (previewUrl) return previewUrl;
    if (!svgSource) return null;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgSource)}`;
  }, [previewUrl, svgSource]);
  // Kroki-render state. Lazy: alleen aangeroepen wanneer de gebruiker
  // expliciet op "Render via Kroki" klikt — geen automatische call bij
  // tab-switch (Kroki kost ms-tot-seconden afhankelijk van diagram-grootte
  // en cache-status).
  const [krokiSvg, setKrokiSvg] = useState<string | null>(null);
  const [krokiError, setKrokiError] = useState<string | null>(null);
  const [krokiBusy, setKrokiBusy] = useState(false);

  // Reset de Kroki-render zodra de gebruiker een ander tabblad kiest
  // of de PlantUML-source verandert (bv. door een nieuw diagram).
  useEffect(() => {
    setKrokiSvg(null);
    setKrokiError(null);
  }, [outputMode, plantumlSource]);

  const renderViaKroki = async () => {
    if (!plantumlSource) return;
    setKrokiBusy(true);
    setKrokiError(null);
    setKrokiSvg(null);
    try {
      const { blob, contentType } = await api.render({
        source: plantumlSource,
        format: "plantuml",
        outputType: "svg",
      });
      if (!contentType.includes("svg")) {
        throw new Error(t("askClaude.errors.unexpectedContentType", { type: contentType }));
      }
      const text = await blob.text();
      // Kroki/PlantUML retourneert bij syntax-fouten meestal een 200
      // met een mini error-banner-SVG (typisch height ≤ 50, tekst
      // bevat "syntax error" / "ParseException"). We detecteren dat
      // en tonen 'm als duidelijke foutmelding i.p.v. een rare
      // dunne strook in de preview.
      const banner = detectKrokiErrorBanner(text);
      if (banner) {
        setKrokiError(banner);
      } else {
        setKrokiSvg(text);
      }
    } catch (err) {
      setKrokiError(err instanceof Error ? err.message : String(err));
    } finally {
      setKrokiBusy(false);
    }
  };

  if (outputMode === "svg") {
    return (
      // Canvaskleur achter het model (zie .ae-diagram-surface in globals.css).
      <div className="ae-diagram-surface flex h-full w-full items-center justify-center overflow-hidden p-3">
        {previewSrc ? (
          <img
            src={previewSrc}
            alt={t("askClaude.preview.alt")}
            className="max-h-full max-w-full object-contain"
          />
        ) : (
          <p className="text-(--ld-muted) text-xs italic">{t("askClaude.preview.noDiagram")}</p>
        )}
      </div>
    );
  }

  if (outputMode === "json") {
    return (
      <pre className="h-full w-full overflow-auto whitespace-pre-wrap break-all p-3 font-mono text-[12px] text-(--ld-text)">
        {summarySource || t("askClaude.preview.noSummary")}
      </pre>
    );
  }

  // PlantUML-modus: bron + render-knop. Bij geslaagde Kroki-render
  // tonen we de SVG inline (zelfde fit-recipe als de svg-modus); anders
  // zien we de plain-text bron.
  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex shrink-0 items-center gap-2 border-(--ld-border) border-b bg-(--ld-surface) px-3 py-1.5 text-xs">
        <button
          type="button"
          className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 text-(--ld-text) hover:bg-(--ld-raised) disabled:opacity-50"
          onClick={() => void renderViaKroki()}
          disabled={!plantumlSource || krokiBusy}
        >
          {krokiBusy
            ? t("askClaude.preview.rendering")
            : krokiSvg
              ? t("askClaude.preview.rerender")
              : t("askClaude.preview.renderKroki")}
        </button>
        {krokiSvg && (
          <button
            type="button"
            className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 text-(--ld-text) hover:bg-(--ld-raised)"
            onClick={() => setKrokiSvg(null)}
            title={t("askClaude.preview.backToSourceTitle")}
          >
            {t("askClaude.preview.backToSource")}
          </button>
        )}
      </div>
      {krokiError && (
        <div className="shrink-0 border-(--ld-control) border-b bg-(--ld-negative-bg) px-3 py-1.5 text-(--ld-negative-ink) text-xs">
          <strong>{t("askClaude.preview.renderError")}</strong>{" "}
          <span className="break-words font-mono">{krokiError}</span>
        </div>
      )}
      <div
        className={`min-h-0 flex-1 overflow-auto ${krokiSvg ? "ae-diagram-surface" : "bg-(--ld-raised)"}`}
      >
        {krokiSvg ? (
          <div
            className="flex h-full w-full items-center justify-center p-3 [&>svg]:h-auto [&>svg]:w-auto [&>svg]:max-h-full [&>svg]:max-w-full"
            // SVG komt uit Kroki via onze worker-proxy (immutable cache).
            dangerouslySetInnerHTML={{ __html: krokiSvg }}
          />
        ) : (
          <pre className="h-full w-full overflow-auto whitespace-pre-wrap break-all p-3 font-mono text-[12px] text-(--ld-text)">
            {plantumlSource || t("askClaude.preview.noPlantuml")}
          </pre>
        )}
      </div>
    </div>
  );
}

/** Waar Vraag Claude heen gaat: rechtstreeks met een ticket, anders via de proxy. */
async function aiTransformTarget(): Promise<{ url: string; headers: Record<string, string> }> {
  const viaProxy = { url: "/api/v1/ai/transform", headers: {} };
  try {
    const res = await fetch("/api/v1/ai/ticket", { method: "POST", credentials: "include" });
    if (!res.ok) return viaProxy;
    const { ticket, origin } = (await res.json()) as { ticket?: string; origin?: string };
    if (!ticket || !origin) return viaProxy;
    return {
      url: `${origin.replace(/\/+$/, "")}/api/v1/ai-direct/transform`,
      headers: { "x-ai-ticket": ticket },
    };
  } catch {
    return viaProxy;
  }
}
